import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Target, Heart, Shield, Zap, Users, MapPin, Headphones } from "lucide-react";

const VALUES = [
  { icon: Target, title: "Enfoque en negocios", body: "No hacemos de todo. Nos especializamos en locales comerciales, oficinas, naves y traspasos porque entendemos el mercado de primera mano." },
  { icon: Heart, title: "Transparencia real", body: "Precios claros, sin letra pequeña. Creemos que la confianza se gana con honestidad en cada interacción." },
  { icon: Shield, title: "Seguridad jurídica", body: "Contratos revisados, trámites gestionados y verificación de contactos para que operes con tranquilidad." },
  { icon: Zap, title: "Tecnología útil", body: "Herramientas que aceleran: alertas inteligentes, métricas de visibilidad y un panel centralizado en MyBizaclia." },
  { icon: Users, title: "Comunidad de profesionales", body: "Agentes, agencias y propietarios que comparten un mismo objetivo: mover el mercado inmobiliario comercial." },
  { icon: Headphones, title: "Cerca de ti", body: "Soporte humano de lunes a sábado. Sabemos que una operación inmobiliaria no espera." },
];

const STATS = [
  { value: "2021", label: "Año de fundación", sub: "Nacimos con una idea clara" },
  { value: "+10.000", label: "Anuncios publicados", sub: "De locales, oficinas, naves y traspasos" },
  { value: "+2.000", label: "Negocios activos", sub: "Anunciantes que confían en nosotros" },
  { value: "48h", label: "Primer contacto medio", sub: "Velocidad que marca la diferencia" },
];

export const Route = createFileRoute("/sobre-bizaclia")({
  head: () => ({
    meta: [
      { title: "Sobre Bizaclia — El portal inmobiliario para negocios" },
      { name: "description", content: "Conoce la historia, misión y valores de Bizaclia. El portal inmobiliario exclusivo para locales comerciales, oficinas, naves y traspasos." },
      { property: "og:title", content: "Sobre Bizaclia — El portal inmobiliario para negocios" },
      { property: "og:description", content: "Conoce la historia, misión y valores de Bizaclia." },
    ],
  }),
  component: SobreBizacliaPage,
});

function SobreBizacliaPage() {
  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div
          aria-hidden
          className="absolute inset-x-0 -top-32 h-[520px] bg-[radial-gradient(ellipse_at_top,theme(colors.primary/15%),transparent_60%)] pointer-events-none"
        />
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 relative">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition mb-10"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver al inicio
          </Link>

          <div className="max-w-3xl">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              Sobre nosotros
            </span>
            <h1 className="mt-4 font-display font-bold text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05]">
              El portal inmobiliario{" "}
              <span className="text-primary">exclusivo</span> para negocios
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              Nacimos para resolver un problema real: encontrar, traspasar, vender o alquilar
              un local comercial sigue siendo más complicado de lo que debería. Bizaclia une
              tecnología, acompañamiento humano y un panel operativo que pone el control en tus manos.
            </p>
          </div>
        </div>
      </section>

      {/* Misión */}
      <section className="py-20 sm:py-24 bg-surface border-y border-border">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
                Nuestra misión
              </span>
              <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight">
                Democratizar el acceso al mercado inmobiliario comercial
              </h2>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Creemos que cualquier persona o empresa debería poder publicar y encontrar
                locales comerciales sin depender de intermediarios opacos ni pagar comisiones
                desproporcionadas. Nuestra plataforma conecta directamente a propietarios,
                inversores y emprendedores con total transparencia.
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                No somos una inmobiliaria tradicional: somos una plataforma tecnológica que pone
                a tu disposición las herramientas que necesitas para gestionar tus operaciones
                de forma autónoma, con el respaldo de un equipo de especialistas cuando lo necesites.
              </p>
            </div>
            <div className="rounded-3xl border border-border bg-card p-8 sm:p-10 relative overflow-hidden">
              <div aria-hidden className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
              <div className="relative space-y-6">
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft shrink-0">
                    <MapPin className="h-5 w-5 text-primary" />
                  </span>
                  <div>
                    <h3 className="font-semibold">Cobertura nacional</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Anuncios en todas las provincias españolas, desde Madrid y Barcelona hasta
                      ciudades medianas con alto potencial comercial.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft shrink-0">
                    <Users className="h-5 w-5 text-primary" />
                  </span>
                  <div>
                    <h3 className="font-semibold">Para todos los perfiles</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Ya seas propietario, inversor, emprendedor o agencia inmobiliaria, tienes
                      un espacio diseñado para ti.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft shrink-0">
                    <Zap className="h-5 w-5 text-primary" />
                  </span>
                  <div>
                    <h3 className="font-semibold">Tecnología a tu servicio</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Alertas inteligentes, panel de operaciones y métricas en tiempo real para
                      que tomes decisiones informadas.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historia */}
      <section className="py-20 sm:py-24">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              Nuestra historia
            </span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight">
              De una idea a una comunidad de miles
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                year: "2021",
                title: "El origen",
                body: "Identificamos la falta de un portal especializado para locales comerciales en España. Idealista y Fotocasa dominaban, pero ninguno entendía las necesidades específicas de negocios. Bizaclia nació para llenar ese vacío.",
              },
              {
                year: "2022-2023",
                title: "Crecimiento y aprendizaje",
                body: "Lanzamos la primera versión del portal y comenzamos a validar con propietarios y agencias. Miles de anuncios publicados nos enseñaron lo que realmente necesitaba el mercado: más transparencia, menos fricción.",
              },
              {
                year: "2024-2026",
                title: "MyBizaclia y consolidación",
                body: "Desarrollamos el panel de gestión MyBizaclia, un sistema integral para que agentes y agencias controlen sus operaciones, contactos y métricas. Hoy somos referencia en el sector inmobiliario comercial.",
              },
            ].map((item) => (
              <div
                key={item.year}
                className="rounded-3xl border border-border bg-card p-8 hover:border-primary/40 transition"
              >
                <span className="font-display text-5xl font-bold text-primary/30">
                  {item.year}
                </span>
                <h3 className="mt-4 font-display font-bold text-xl tracking-tight">
                  {item.title}
                </h3>
                <p className="mt-3 text-muted-foreground leading-relaxed text-sm">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-20 sm:py-24 bg-surface border-y border-border">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              Nuestros valores
            </span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight">
              Lo que nos define cada día
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUES.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-3xl border border-border bg-card p-6 hover:border-primary/40 transition"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-soft">
                    <Icon className="h-5 w-5 text-primary" />
                  </span>
                  <h3 className="mt-5 font-display font-bold text-lg tracking-tight">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {item.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 sm:py-24">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">
              En cifras
            </span>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight">
              Resultados que hablan por sí solos
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {STATS.map((s) => (
              <div
                key={s.label}
                className="rounded-3xl border border-border bg-card p-6 hover:border-primary/40 transition"
              >
                <div className="font-display font-bold text-4xl sm:text-5xl text-primary tracking-tight">
                  {s.value}
                </div>
                <div className="mt-3 font-semibold text-foreground">{s.label}</div>
                <p className="mt-1 text-sm text-muted-foreground">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-24">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6">
          <div className="rounded-3xl bg-ink text-ink-foreground p-10 sm:p-16 text-center relative overflow-hidden">
            <div aria-hidden className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-primary/30 blur-3xl" />
            <div aria-hidden className="absolute -left-24 -bottom-24 h-80 w-80 rounded-full bg-primary/20 blur-3xl" />
            <div className="relative">
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">
                ¿Formamos parte de tu próxima operación?
              </h2>
              <p className="mt-4 text-ink-foreground/70 max-w-xl mx-auto">
                Explora el portal, publica tu primer anuncio o contacta con nuestro equipo.
                Estamos aquí para ayudarte a mover tu negocio.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/portal"
                  className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition"
                >
                  Explorar locales
                </Link>
                <Link
                  to="/mybizaclia"
                  search={{ tab: "soporte" }}
                  className="inline-flex items-center justify-center rounded-full border border-white/20 px-6 py-3 text-sm font-semibold hover:bg-white/10 transition"
                >
                  Hablar con un asesor
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
